import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import "../../styles/client.css";
import { useToast } from "../../hooks/useToast";
import { Toaster } from "../../components/ui/toaster";
import ClientAppointmentView from "../../components/views/ClientAppointmentView";
import AppointmentConfirmationView from "../../components/views/AppointmentConfirmationView";
import VisioConferenceView from "../../components/views/VisioConferenceView";
import VisioRecapView from "../../components/views/VisioRecapView";
import axios from "axios";

const ClientV2 = () => {
  const { token } = useParams();
  const { toast } = useToast();
  const [currentView, setCurrentView] = useState("appointment");
  const [appointment, setAppointment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [folderData, setFolderData] = useState(null);
  const [loadingFolder, setLoadingFolder] = useState(true);

  // Charger l'état du dossier depuis le token en URL ou depuis localStorage
  useEffect(() => {
    const loadFolderData = async () => {
      if (token) {
        // Fonction pour vérifier l'état du dossier
        setLoadingFolder(true);
        try {
          const response = await axios.get(`/api/folder/status/${token}`);

          if (response.data && response.data.success) {
            const folder = response.data.folder;
            setFolderData(folder);

            // Déterminer la vue selon l'état du dossier
            // Visio instantanée : accès direct à la visio, sans RDV ni créneau (T2)
            if (folder.f_visio_instant) {
              setCurrentView("visio");
            } else if (
              folder.f_date_appointment &&
              folder.f_hour_appointment &&
              !folder.f_appointment_canceled
            ) {
              // Le RDV est stocké en heure locale de l'EXPERT. Le serveur nous
              // donne l'instant absolu (appointmentUtc) et l'heure telle que
              // l'assuré doit la lire (appointmentLocal, dans le fuseau de son
              // agence).
              //
              // Sans ça, `new Date("date" + "T" + "heure")` interprétait l'heure
              // dans le fuseau du NAVIGATEUR : pour un assuré antillais, la
              // visio se serait ouverte avec 6 h de décalage.
              const local = folder.appointmentLocal || {
                date: folder.f_date_appointment,
                time: folder.f_hour_appointment,
              };
              const dateTime = folder.appointmentUtc
                ? new Date(folder.appointmentUtc)
                : new Date(
                    `${folder.f_date_appointment}T${folder.f_hour_appointment}`
                  );

              setAppointment({
                date: new Date(`${local.date}T00:00:00`),
                time: String(local.time).slice(0, 5),
                referenceNumber: folder.f_reference,
                clientName: folder.f_insured_name,
                dateTime,
                timezone: response.data.timezone || null,
              });

              setCurrentView("confirmation");
            } else {
              // Aucun RDV pris (ou RDV annulé), afficher la prise de RDV
              if (folder.f_appointment_canceled) {
                localStorage.removeItem("xpertvisio-appointment");
              }
              setCurrentView("appointment");
            }
          } else {
            toast({
              variant: "destructive",
              title: "Erreur",
              description: "Dossier non trouvé ou inaccessible",
            });
          }
        } catch (error) {
          console.error("Erreur lors de la vérification du dossier:", error);
          toast({
            variant: "destructive",
            title: "Erreur",
            description: "Impossible de vérifier l'état du dossier",
          });
        } finally {
          setLoadingFolder(false);
        }
      } else {
        // Fallback: charger depuis localStorage si pas de token
        const savedAppointment = localStorage.getItem("xpertvisio-appointment");
        if (savedAppointment) {
          try {
            const parsedAppointment = JSON.parse(savedAppointment);
            if (parsedAppointment.date) {
              parsedAppointment.date = new Date(parsedAppointment.date);
            }
            if (parsedAppointment.dateTime) {
              parsedAppointment.dateTime = new Date(parsedAppointment.dateTime);
            }
            setAppointment(parsedAppointment);
            setCurrentView("confirmation");
          } catch (error) {
            console.error("Erreur lors du chargement du rendez-vous:", error);
            localStorage.removeItem("xpertvisio-appointment");
          }
        }
        setLoadingFolder(false);
      }
    };

    loadFolderData();
  }, [token, toast]);

  // Étape 1: Prendre un rendez-vous
  const handleAppointmentBooked = async (appointmentData) => {
    setIsLoading(true);

    try {
      if (token) {
        // Mettre à jour le dossier avec les informations de RDV
        const response = await axios.put(`/api/folder/appointment/${token}`, {
          date: format(appointmentData.appointment.date, "yyyy-MM-dd"),
          time: appointmentData.appointment.time,
        });

        if (response.data && response.data.success) {
          const appointmentToSave = {
            ...appointmentData.appointment,
            referenceNumber: folderData.f_reference,
            bookedAt: new Date().toISOString(),
          };

          setAppointment(appointmentToSave);
          setCurrentView("confirmation");

          toast({
            title: "Rendez-vous confirmé",
            description:
              "Votre rendez-vous d'expertise a été enregistré avec succès",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Créneau indisponible",
            description: response.data.message || "Erreur lors de la sauvegarde",
          });
          return;
        }
      } else {
        // Fallback: simulation locale
        const appointmentToSave = {
          ...appointmentData.appointment,
          referenceNumber: `RDV-${Date.now()}`,
          bookedAt: new Date().toISOString(),
        };

        localStorage.setItem(
          "xpertvisio-appointment",
          JSON.stringify(appointmentToSave)
        );
        setAppointment(appointmentToSave);
        setCurrentView("confirmation");

        toast({
          title: "Rendez-vous confirmé",
          description:
            "Votre rendez-vous d'expertise a été enregistré avec succès",
        });
      }
    } catch (error) {
      console.error("Erreur lors de la prise de RDV:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de la prise de rendez-vous",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Étape 2: Entrer en visioconférence (le jour J)
  const handleEnterVisio = () => {
    setCurrentView("visio");
    toast({
      title: "Connexion en cours",
      description: "Démarrage de la visioconférence...",
    });
  };

  // Étape 3: Terminer l'appel et afficher le récapitulatif
  const handleEndCall = () => {
    setCurrentView("recap");
    toast({
      title: "Appel terminé",
      description: "La visioconférence s'est terminée",
    });
  };

  // En visio instantanée il n'y a pas de RDV : la vue de repli reste la visio.
  const getFallbackView = () =>
    folderData && folderData.f_visio_instant ? "visio" : "confirmation";

  // Étape 4: Quitter le récapitulatif et revenir à la confirmation
  const handleRecapDone = () => {
    setCurrentView(getFallbackView());
  };

  // Retour depuis la visio vers la confirmation
  const handleBackToConfirmation = () => {
    setCurrentView(getFallbackView());
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case "appointment":
        return (
          <ClientAppointmentView
            onClientValidated={handleAppointmentBooked}
            isLoading={isLoading}
            token={token}
            canceled={!!(folderData && folderData.f_appointment_canceled)}
          />
        );

      case "confirmation":
        return (
          <AppointmentConfirmationView
            appointment={appointment}
            onEnterVisio={handleEnterVisio}
          />
        );

      case "visio":
        return (
          <VisioConferenceView
            appointment={appointment}
            onEndCall={handleEndCall}
            onGoBack={handleBackToConfirmation}
            autoStart={true}
          />
        );

      case "recap":
        return <VisioRecapView onDone={handleRecapDone} />;

      default:
        return null;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-md mx-auto space-y-4">
          {/* Header */}
          <div className="text-center space-y-1 pt-6 pb-4">
            <h1 className="text-lg font-bold text-gray-900">
              Votre plateforme d'expertise à distance
            </h1>
          </div>

          {/* Loading State */}
          {loadingFolder ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement du dossier...</p>
            </div>
          ) : (
            /* Dynamic Form Views */
            renderCurrentView()
          )}

          {/* Footer */}
          <div className="text-center text-xs text-gray-500 space-y-1 pb-4">
            <p>xpertvisio.fr</p>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster />
    </>
  );
};

export default ClientV2;
